import { useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useTenant } from '../providers/TenantProvider';

export const SetupPage = () => {
  const { user, tenantId, refreshTenants } = useTenant();
  const [targetEmail, setTargetEmail] = useState('');
  const [targetTenantId, setTargetTenantId] = useState(tenantId || 'tenant-demo-gym');
  const [role, setRole] = useState<string>('staff');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleAddMembership = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const functions = getFunctions();
      const addTenantMembership = httpsCallable(functions, 'addTenantMembership');

      const result = await addTenantMembership({
        tenantId: targetTenantId,
        email: targetEmail || user?.email,
        password: password || undefined,
        roles: [role],
        firstName: firstName || undefined,
        lastName: lastName || undefined,
      });

      const data = result.data as { message?: string };
      setSuccess(data.message || 'Tenant membership added.');
      await refreshTenants();
    } catch (err: any) {
      console.error('Error adding membership:', err);
      setError(err.message || 'Failed to add membership');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="sm">
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            Tenant Membership Setup
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Add an existing or new auth user to a tenant. You must already be an owner of that tenant.
          </Typography>

          <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="caption" display="block">
              <Box component="strong" sx={{ fontWeight: 700 }}>Your User ID:</Box> {user?.uid}
            </Typography>
            <Typography variant="caption" display="block">
              <Box component="strong" sx={{ fontWeight: 700 }}>Your Email:</Box> {user?.email}
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleAddMembership}>
            <TextField
              fullWidth
              label="Email"
              value={targetEmail}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setTargetEmail(event.target.value)}
              placeholder={user?.email || ''}
              sx={{ mb: 2 }}
              helperText="Leave empty to add your signed-in email"
            />

            <TextField
              fullWidth
              label="Tenant ID"
              value={targetTenantId}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setTargetTenantId(event.target.value)}
              required
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                value={firstName}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setFirstName(event.target.value)}
              />
              <TextField
                fullWidth
                label="Last Name"
                value={lastName}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setLastName(event.target.value)}
              />
            </Box>

            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setPassword(event.target.value)}
              sx={{ mb: 2 }}
              helperText="Required only when creating a brand-new sign-in account"
            />

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Role</InputLabel>
              <Select value={role} onChange={(event: SelectChangeEvent) => setRole(event.target.value)} label="Role">
                <MenuItem value="owner">Owner</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="staff">Staff</MenuItem>
                <MenuItem value="coach">Coach</MenuItem>
                <MenuItem value="member">Member</MenuItem>
              </Select>
            </FormControl>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Button type="submit" variant="contained" fullWidth disabled={loading || !targetTenantId}>
              {loading ? 'Adding Membership...' : 'Add Tenant Membership'}
            </Button>
          </Box>

          <Alert severity="info" sx={{ mt: 3 }}>
            For a clean local environment, use `pnpm seed` to create demo tenants and owner memberships.
          </Alert>
        </Paper>
      </Container>
    </Box>
  );
};

export default SetupPage;
